<script setup>
import { reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, minValue, maxValue } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  wahaInboxes: useMapGetter('inboxes/getWahaInboxes'),
};

const initialState = {
  title: '',
  selectedInboxIds: [],
  messages: [{ type: 'text', content: '', media_url: '' }],
  messageIntervalSeconds: 120,
  scheduledAt: null,
  selectedAudience: [],
};

const state = reactive({ ...initialState });

// Validation rules
const rules = {
  title: { required, minLength: minLength(1) },
  selectedInboxIds: { required, minLength: minLength(1) },
  scheduledAt: { required },
  selectedAudience: { required, minLength: minLength(1) },
  messageIntervalSeconds: {
    required,
    minValue: minValue(120),
    maxValue: maxValue(3600),
  },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

// Inbox options with status
const inboxOptions = computed(
  () =>
    formState.wahaInboxes.value?.map(inbox => ({
      value: inbox.id,
      label: inbox.name,
      phoneNumber: inbox.waha_phone_number,
      isOnline: inbox.waha_session_status?.toUpperCase() === 'WORKING',
    })) ?? []
);

// Audience options
const audienceList = computed(
  () =>
    formState.labels.value?.map(label => ({
      value: label.id,
      label: label.title,
    })) ?? []
);

// Interval options - i18n keys instead of hardcoded Portuguese
const intervalOptions = computed(() => [
  {
    value: 120,
    label: t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.OPTIONS.TWO_MIN'),
  },
  {
    value: 180,
    label: t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.OPTIONS.THREE_MIN'),
  },
  {
    value: 300,
    label: t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.OPTIONS.FIVE_MIN'),
  },
  {
    value: 600,
    label: t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.OPTIONS.TEN_MIN'),
  },
  {
    value: 900,
    label: t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.OPTIONS.FIFTEEN_MIN'),
  },
  {
    value: 1800,
    label: t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.OPTIONS.THIRTY_MIN'),
  },
  {
    value: 3600,
    label: t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.OPTIONS.ONE_HOUR'),
  },
]);

// Message type options - i18n keys instead of hardcoded Portuguese
const messageTypeOptions = computed(() => [
  { value: 'text', label: t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.TYPES.TEXT') },
  {
    value: 'image',
    label: t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.TYPES.IMAGE'),
  },
  {
    value: 'video',
    label: t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.TYPES.VIDEO'),
  },
  {
    value: 'document',
    label: t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.TYPES.DOCUMENT'),
  },
]);

// Computed for summary
const selectedInboxCount = computed(() => state.selectedInboxIds.length);
const messageCount = computed(
  () => state.messages.filter(m => m.content).length
);

// Methods
const addMessage = () => {
  if (state.messages.length < 5) {
    state.messages.push({ type: 'text', content: '', media_url: '' });
  }
};

const removeMessage = index => {
  if (state.messages.length > 1) {
    state.messages.splice(index, 1);
  }
};

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WAHA.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inboxes: getErrorMessage('selectedInboxIds', 'INBOXES'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasValidMessages = computed(() => {
  return state.messages.some(msg => {
    if (!msg.content) return false;
    if (['image', 'video', 'document'].includes(msg.type) && !msg.media_url) {
      return false;
    }
    return true;
  });
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasValidMessages.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, {
    ...initialState,
    messages: [{ type: 'text', content: '', media_url: '' }],
  });
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  // Filter valid messages only
  const validMessages = state.messages.filter(msg => {
    if (!msg.content) return false;
    return true;
  });

  // Use first selected inbox as primary (for compatibility)
  const primaryInboxId = state.selectedInboxIds[0];

  return {
    title: state.title,
    message: validMessages[0]?.content || '',
    inbox_id: primaryInboxId,
    waha_inbox_ids: state.selectedInboxIds,
    waha_messages: validMessages,
    message_interval_seconds: state.messageIntervalSeconds,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience.map(id => ({
      id,
      type: 'Label',
    })),
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid || !hasValidMessages.value) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};

// Toggle inbox selection
const toggleInbox = inboxId => {
  const index = state.selectedInboxIds.indexOf(inboxId);
  if (index === -1) {
    if (state.selectedInboxIds.length < 10) {
      state.selectedInboxIds.push(inboxId);
    }
  } else {
    state.selectedInboxIds.splice(index, 1);
  }
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <!-- Title -->
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WAHA.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WAHA.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <!-- WAHA Inboxes Multi-select -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.INBOXES.LABEL') }}
      </label>
      <div class="space-y-2 max-h-40 overflow-y-auto">
        <label
          v-for="inbox in inboxOptions"
          :key="inbox.value"
          class="flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors"
          :class="[
            state.selectedInboxIds.includes(inbox.value)
              ? 'border-n-blue-text bg-n-blue-alpha-1'
              : 'border-n-weak hover:border-n-slate-7',
            !inbox.isOnline && 'opacity-60',
          ]"
          @click.prevent="toggleInbox(inbox.value)"
        >
          <input
            type="checkbox"
            :checked="state.selectedInboxIds.includes(inbox.value)"
            class="rounded"
            @click.stop
            @change="toggleInbox(inbox.value)"
          />
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12 truncate">
              {{ inbox.label }}
            </span>
            <span v-if="inbox.phoneNumber" class="ml-2 text-xs text-n-slate-11">
              {{ inbox.phoneNumber }}
            </span>
          </div>
          <span
            v-if="inbox.isOnline"
            class="text-xs px-2 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11"
          >
            {{ t('CAMPAIGN.WAHA.CREATE.FORM.INBOXES.ONLINE') }}
          </span>
          <span
            v-else
            class="text-xs px-2 py-0.5 rounded-full bg-n-ruby-3 text-n-ruby-11"
          >
            {{ t('CAMPAIGN.WAHA.CREATE.FORM.INBOXES.OFFLINE') }}
          </span>
        </label>
      </div>
      <p v-if="formErrors.inboxes" class="text-xs text-n-ruby-11">
        {{ formErrors.inboxes }}
      </p>
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.INBOXES.INFO') }}
      </p>
    </div>

    <!-- Messages Editor -->
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.LABEL') }}
      </label>
      <div class="space-y-3">
        <div
          v-for="(msg, index) in state.messages"
          :key="index"
          class="p-3 border border-n-weak rounded-lg"
        >
          <div class="flex justify-between items-center mb-2">
            <div class="flex items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.MESSAGE_LABEL', {
                    n: index + 1,
                  })
                }}
              </span>
              <select
                v-model="msg.type"
                class="text-xs px-2 py-1 rounded border border-n-weak bg-n-alpha-1 text-n-slate-12"
              >
                <option
                  v-for="opt in messageTypeOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </option>
              </select>
            </div>
            <button
              v-if="state.messages.length > 1"
              type="button"
              class="text-xs text-n-ruby-11 hover:text-n-ruby-12"
              @click="removeMessage(index)"
            >
              {{ t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.REMOVE') }}
            </button>
          </div>
          <TextArea
            v-model="msg.content"
            :placeholder="t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.PLACEHOLDER')"
            :rows="3"
            class="w-full"
          />
          <Input
            v-if="['image', 'video', 'document'].includes(msg.type)"
            v-model="msg.media_url"
            :label="t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.MEDIA_URL')"
            :placeholder="
              t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.MEDIA_URL_PLACEHOLDER')
            "
            class="mt-2"
          />
        </div>
      </div>
      <button
        v-if="state.messages.length < 5"
        type="button"
        class="text-sm text-n-blue-text hover:text-n-blue-text-hover"
        @click="addMessage"
      >
        + {{ t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.ADD') }}
      </button>
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGES.INFO') }}
      </p>
    </div>

    <!-- Interval -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.LABEL') }}
      </label>
      <ComboBox
        v-model="state.messageIntervalSeconds"
        :options="intervalOptions"
        :placeholder="t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.PLACEHOLDER')"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.INTERVAL.INFO') }}
      </p>
    </div>

    <!-- Audience -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WAHA.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WAHA.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <!-- Scheduled At -->
    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WAHA.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Summary -->
    <div class="p-4 bg-n-alpha-1 rounded-lg">
      <h4 class="text-sm font-medium text-n-slate-12 mb-2">
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.SUMMARY.TITLE') }}
      </h4>
      <ul class="text-sm text-n-slate-11 space-y-1">
        <li>
          {{ t('CAMPAIGN.WAHA.CREATE.FORM.SUMMARY.INBOXES') }}:
          {{ selectedInboxCount }}
        </li>
        <li>
          {{ t('CAMPAIGN.WAHA.CREATE.FORM.SUMMARY.MESSAGES') }}:
          {{ messageCount }} ({{
            t('CAMPAIGN.WAHA.CREATE.FORM.SUMMARY.RANDOMIZED')
          }})
        </li>
        <li>
          {{ t('CAMPAIGN.WAHA.CREATE.FORM.SUMMARY.INTERVAL') }}:
          {{
            intervalOptions.find(o => o.value === state.messageIntervalSeconds)
              ?.label
          }}
        </li>
      </ul>
    </div>

    <!-- Buttons -->
    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WAHA.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WAHA.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
